// ============================================================================
// My subs — the landing tab (TCSD-0371 ¶1, prototype screen 1).
// Scope toggle (remembered per person) · four tiles: Bench · At risk · Leads
// waiting · No sub · the Needs-a-status row · search · Share my application link
// · the list of Bench and Active subs in scope. Leads never appear on this list —
// they live in Pipeline (TCSD-0373 ¶2).
// ============================================================================

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { EmptyState, ErrorState, Skeleton } from '../../../shared/components';
import { inScope, needsAStatus, shortMarkets, type HubSub, type HubScope, type NoSubBuilding } from './use-hub-data';
import { subStatusLabel } from '../stages';

type Tile = 'bench' | 'risk' | 'nosub';

export function MySubsTab({
  data,
  scope,
  setScope,
  onShare,
  onLeads,
  initialTile,
}: {
  data: {
    me: string | null;
    subs: HubSub[];
    myMarkets: string[];
    noSub: NoSubBuilding[];
    loading: boolean;
    error: string | null;
    refetch: () => void;
  };
  scope: HubScope;
  setScope: (s: HubScope) => void;
  onShare: () => void;
  onLeads: () => void;
  initialTile?: Tile | null;
}) {
  const navigate = useNavigate();
  const [tile, setTile] = useState<Tile | null>(initialTile ?? null);
  const [q, setQ] = useState('');
  const canScope = data.myMarkets.length > 0;

  const scoped = useMemo(
    () => data.subs.filter((s) => inScope(s, scope, data.myMarkets, data.me)),
    [data.subs, scope, data.myMarkets, data.me],
  );
  const bench = scoped.filter((s) => s.on_bench);
  const risk = scoped.filter((s) => s.status === 'Active' && s.risk_reason);
  const leads = scoped.filter((s) => s.status === 'Lead');
  const noSub = data.noSub.filter((b) => scope === 'all' || (b.market !== null && data.myMarkets.includes(b.market)));
  const needs = scoped.filter(needsAStatus);

  const listed = useMemo(() => {
    let rows = scoped.filter((s) => s.status === 'Ready' || s.status === 'Active');
    if (tile === 'bench') rows = rows.filter((s) => s.on_bench);
    if (tile === 'risk') rows = rows.filter((s) => s.status === 'Active' && s.risk_reason);
    const term = q.trim().toLowerCase();
    if (term) {
      const digits = term.replace(/[^0-9]/g, '');
      rows = rows.filter(
        (s) =>
          s.name.toLowerCase().includes(term) ||
          s.building_names.some((b) => b.toLowerCase().includes(term)) ||
          (digits.length >= 3 && (s.phone ?? '').replace(/[^0-9]/g, '').includes(digits)),
      );
    }
    return rows;
  }, [scoped, tile, q]);

  if (data.error) return <ErrorState title="Couldn't load your subs" body={data.error} onRetry={data.refetch} />;

  const tiles: Array<{ key: Tile | 'leads'; label: string; n: number; tone: string }> = [
    { key: 'bench', label: 'Bench', n: bench.length, tone: '' },
    { key: 'risk', label: 'At risk', n: risk.length, tone: 'text-st-warn-fg-strong' },
    { key: 'leads', label: 'Leads waiting', n: leads.length, tone: '' },
    { key: 'nosub', label: 'No sub', n: noSub.length, tone: 'text-st-danger-fg' },
  ];

  return (
    <div className="space-y-3">
      {canScope && (
        <div role="group" aria-label="Which subs" className="grid grid-cols-2 rounded-lg border border-tcs-border p-0.5 text-sm">
          {(['mine', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={scope === s}
              onClick={() => {
                setScope(s);
                setTile(null);
              }}
              className={`rounded-md py-1.5 font-medium ${scope === s ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary'}`}
            >
              {s === 'mine' ? 'My markets' : 'All TCS'}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-4 gap-1.5">
        {tiles.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-pressed={tile === t.key}
            data-tile={t.key}
            onClick={() => {
              if (t.key === 'leads') onLeads();
              else setTile((cur) => (cur === t.key ? null : (t.key as Tile)));
            }}
            className={`flex flex-col items-start justify-start rounded-lg border p-2 text-left ${
              tile === t.key ? 'border-tcs-text bg-tcs-bg-muted' : 'border-tcs-border bg-tcs-raised'
            }`}
          >
            <div className={`font-mono text-xl font-bold ${t.tone || 'text-tcs-text'}`}>{data.loading ? '–' : t.n}</div>
            <div className="text-[11px] leading-tight text-tcs-text-muted">{t.label}</div>
          </button>
        ))}
      </div>

      {needs.length > 0 && (
        <Link
          to={`/people/subcontractors/needs-status?scope=${scope}`}
          className="flex items-center justify-between rounded-lg border border-st-warn-border bg-st-warn-bg px-3 py-2 text-sm text-st-warn-fg-strong"
        >
          <span>
            <b>{needs.length} need a status</b> · active, no building
          </span>
          <span className="inline-flex items-center">
            Sort <ChevronRight className="h-4 w-4" />
          </span>
        </Link>
      )}

      <label className="flex items-center gap-2 rounded-lg border border-tcs-border bg-tcs-field px-3 py-2">
        <Search className="h-4 w-4 text-tcs-text-muted" />
        <input
          aria-label="Search subs"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, phone, building…"
          className="w-full bg-transparent text-sm text-tcs-text outline-none"
        />
      </label>

      <button type="button" onClick={onShare} className="text-sm font-medium text-st-ok-fg-strong">
        Share my application link ›
      </button>

      {data.loading ? (
        <Skeleton className="h-40 w-full" />
      ) : tile === 'nosub' ? (
        noSub.length === 0 ? (
          <EmptyState title="Every building has a sub" body="Nothing here." />
        ) : (
          <ul className="divide-y divide-tcs-border rounded-lg border border-tcs-border">
            {noSub.map((b) => (
              <li key={b.facility_id}>
                <button
                  type="button"
                  onClick={() => navigate(`/account-management/facilities/${b.facility_id}`)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-tcs-text">{b.facility_name}</div>
                    <div className="text-xs text-tcs-text-muted">
                      {shortMarkets(b.market ? [b.market] : [])} · no sub assigned{b.status !== 'Active' ? ' · not open yet' : ''}
                    </div>
                  </div>
                  <span className="rounded-full bg-st-danger-bg px-2 py-0.5 text-[11px] font-medium text-st-danger-fg">No sub</span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : listed.length === 0 ? (
        <EmptyState title="Nothing here" body={q ? 'No sub matches that search.' : 'No subs in this view.'} />
      ) : (
        <ul className="divide-y divide-tcs-border rounded-lg border border-tcs-border">
          {listed.map((s) => (
            <li key={s.provider_id}>
              <button
                type="button"
                data-sub={s.provider_id}
                onClick={() => navigate(`/people/subcontractors/${s.provider_id}`)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-tcs-text">{s.name}</div>
                  <div className="truncate text-xs text-tcs-text-muted">
                    {[shortMarkets(s.markets) || 'no market', s.live_buildings ? `${s.live_buildings} building${s.live_buildings === 1 ? '' : 's'}` : 'no building', s.risk_reason]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <SubChip s={s} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One chip per row: At risk > Bench > Projects > Tier. */
export function SubChip({ s }: { s: HubSub }) {
  let text: string;
  let cls: string;
  if (s.status === 'Active' && s.risk_reason) {
    text = 'At risk';
    cls = 'bg-st-warn-bg text-st-warn-fg-strong';
  } else if (s.on_bench) {
    text = 'Bench';
    cls = 'bg-st-ok-bg text-st-ok-fg-strong';
  } else if (s.does_projects && !s.does_nightly) {
    text = 'Projects';
    cls = 'bg-tcs-bg-muted text-tcs-text-secondary';
  } else if (s.tier) {
    text = `Tier ${s.tier}`;
    cls = 'bg-tcs-bg-muted text-tcs-text';
  } else {
    text = subStatusLabel(s.status);
    cls = 'bg-tcs-bg-muted text-tcs-text-secondary';
  }
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{text}</span>;
}
